import { supabase } from "@/integrations/supabase/client";
import { processImageForUpload, validateImage } from "./imageProcessing";
import { isNativePlatform } from "./nativePlugins";
import { v4 as uuidv4 } from "uuid";

/**
 * Uploads an image to Supabase storage
 * @param file The image file to upload
 * @param bucketName The storage bucket name
 * @param folderPath Optional folder path within the bucket
 * @returns The URL of the uploaded image or null if upload failed
 */
export const uploadImage = async (
  file: File,
  bucketName: string = 'properties',
  folderPath: string = ''
): Promise<string | null> => {
  const isNative = isNativePlatform();
  console.log(`[Upload] Starting upload for: ${file.name} (${file.size} bytes, native: ${isNative})`);
  
  try {
    // Validate the image
    const validationError = validateImage(file, 10); // 10MB max
    if (validationError) {
      console.error(`[Upload] Validation failed: ${validationError}`);
      throw new Error(validationError);
    }

    // Process the image before upload (resize/compress)
    // processImageForUpload now returns the original file as fallback if processing fails
    let processedImage: Blob | File;
    try {
      processedImage = await processImageForUpload(file, 1200, 1200, false);
      console.log(`[Upload] Image processed successfully: ${processedImage instanceof File ? 'original file' : 'resized blob'}`);
    } catch (processingError) {
      console.warn('[Upload] Image processing threw error, using original file:', processingError);
      processedImage = file; // Fallback to original file
    }

    // Create a unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    
    // Create the full path including any folders
    const filePath = folderPath 
      ? `${folderPath.replace(/^\/|\/$/g, '')}/${fileName}`
      : fileName;

    console.log(`[Upload] Uploading to: ${bucketName}/${filePath}`);

    // Upload the processed image to Supabase
    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, processedImage, {
        contentType: file.type,
        upsert: false
      });

    if (error) {
      console.error("[Upload] Supabase upload error:", error);
      throw new Error(`Failed to upload image: ${error.message}`);
    }

    // Get the public URL for the uploaded image
    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(data.path);

    console.log(`[Upload] Successfully uploaded: ${file.name} -> ${publicUrl}`);
    return publicUrl;
  } catch (error) {
    console.error("[Upload] Error in uploadImage:", error);
    return null;
  }
};

/**
 * Uploads multiple images to Supabase storage in batches
 * @param files Array of image files to upload
 * @param bucketName The storage bucket name
 * @param folderPath Optional folder path within the bucket
 * @param batchSize Number of images to upload simultaneously (default: 5)
 * @param onProgress Optional callback for progress updates
 * @returns Array of URLs of the uploaded images
 */
export const uploadMultipleImages = async (
  files: File[],
  bucketName: string = 'properties',
  folderPath: string = '',
  batchSize: number = 5,
  onProgress?: (completed: number, total: number) => void
): Promise<string[]> => {
  const isNative = isNativePlatform();
  const allResults: string[] = [];
  const totalFiles = files.length;
  
  // On native platforms, use smaller batch size to reduce memory pressure
  const effectiveBatchSize = isNative ? Math.min(batchSize, 2) : batchSize;
  
  console.log(`[Upload] Starting batch upload of ${totalFiles} images (batch size: ${effectiveBatchSize}, native: ${isNative})`);
  
  // Process images in batches to avoid overwhelming the browser/network
  for (let i = 0; i < files.length; i += effectiveBatchSize) {
    const batch = files.slice(i, i + effectiveBatchSize);
    console.log(`[Upload] Processing batch ${Math.floor(i / effectiveBatchSize) + 1}: ${batch.length} images`);
    
    const batchPromises = batch.map(file => uploadImage(file, bucketName, folderPath));
    const batchResults = await Promise.all(batchPromises);
    
    // Filter out null results (failed uploads) and add to final array
    const successfulUploads = batchResults.filter(url => url !== null) as string[];
    const failedCount = batchResults.length - successfulUploads.length;
    
    if (failedCount > 0) {
      console.warn(`[Upload] Batch had ${failedCount} failed uploads`);
    }
    
    allResults.push(...successfulUploads);
    
    // Report progress
    if (onProgress) {
      onProgress(Math.min(i + effectiveBatchSize, totalFiles), totalFiles);
    }
  }
  
  console.log(`[Upload] Batch upload complete: ${allResults.length}/${totalFiles} successful`);
  return allResults;
};

/**
 * Deletes an image from Supabase storage
 * @param url The full URL of the image to delete
 * @param bucketName The storage bucket name
 * @returns True if deletion was successful, false otherwise
 */
export const deleteImage = async (
  url: string,
  bucketName: string = 'properties'
): Promise<boolean> => {
  try {
    // Extract the path from the URL
    const pathMatch = url.match(new RegExp(`${bucketName}/([^?]+)`));
    if (!pathMatch || !pathMatch[1]) {
      throw new Error("Could not extract file path from URL");
    }
    
    const filePath = pathMatch[1];
    
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
      
    if (error) {
      console.error("Image deletion error:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in deleteImage:", error);
    return false;
  }
};
